import {
    formatPermissionName,
    type IPermissionActionDescriptor,
} from '@experiences/interfaces';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { ApTooltip } from '@uipath/portal-shell-react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useHasAnyPermission } from '../../../container/UserAuthPDPModelProvider/UserAuthPDPModelProvider';

const useStyles = makeStyles(() =>
    createStyles({
        disabled: {
            pointerEvents: 'none',
            opacity: 0.4,
            userSelect: 'none',
        },
    }),
);

interface UiPermissionGuardProps {
    requiredActions: IPermissionActionDescriptor[];
    tenantId?: string;
    children: React.ReactNode;
    className?: string;
    'data-cy'?: string;
}

export const UiPermissionGuard: React.FC<UiPermissionGuardProps> = ({
    requiredActions,
    tenantId,
    children,
    className,
    'data-cy': dataCy = 'ui-permission-guard',
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const hasPermission = useHasAnyPermission(requiredActions, tenantId);

    const tooltipContent = useMemo(() =>
        translate(
            { id: 'CLIENT_PERMISSION_GUARD_INSUFFICIENT_PERMISSIONS' },
            { permissions: requiredActions.map(formatPermissionName).join(', ') },
        ), [ translate, requiredActions ]);

    if (hasPermission) {
        return <>
            {children}
        </>;
    }

    return (
        <ApTooltip content={tooltipContent}>
            <div
                className={`${classes.disabled}${className ? ` ${className}` : ''}`}
                aria-disabled="true"
                data-cy={dataCy}
            >
                {children}
            </div>
        </ApTooltip>
    );
};
